/**
 * The enterprise relationship graph, and who governs a repository.
 *
 * The screen exists to make one distinction visible that a diagram alone
 * cannot: the graph describes reality and may be many-to-many, while governance
 * ownership is a separate resolution over it for a stated purpose and moment
 * that returns exactly one answer or refuses.
 *
 * So the refusals are the point. `ambiguous_ownership` is not an error state
 * dressed in red — it is a legitimate answer that names the candidates somebody
 * has to reconcile, and it is rendered as an answer. The evidence is shown for
 * the same reason: an answer is evidence, or it is an opinion.
 *
 * A second kind of truthfulness is owed by the picture itself. A tier reads as
 * "these are the organizations"; a relationships tab reads as "these are its
 * relationships". Neither is something the platform said. It said it returned
 * as many as it was asked for and holds more — so wherever that is what
 * happened, the screen says so and offers the rest.
 */

import { useCallback, useMemo, useState } from "react";
import { api } from "../api/client";
import type {
  CanonicalEntity,
  CanonicalEntityType,
  EntityRelationship,
  GovernanceAssignment,
  GovernanceContext,
  GovernanceResolution,
  Page,
} from "../api/types";
import { usePagedCollection, type PagedCollection } from "../api/useCollection";
import { useResource, type Resource } from "../api/useResource";
import {
  Card,
  Fact,
  Facts,
  Identifier,
  Mono,
  More,
  Pill,
  TabPanel,
  Tabs,
  when,
  type Tone,
} from "../components/primitives";
import { Empty, Show } from "../components/states";
import { useSession } from "../session/SessionProvider";

/**
 * The window the graph reads in, unchanged from before it could continue.
 *
 * Two hundred is a large window, and a large window is the easiest place to
 * forget that there is a window at all: four tiers of a couple of dozen nodes
 * never reach it, so the boundary is invisible until an estate is big enough
 * for its absence to matter. It still ends somewhere, and the screen says where.
 */
const GRAPH_PAGE = 200;

/** The page size for governance decisions on one subject. Unchanged. */
const DECISIONS_PAGE = 50;

const OUTCOME_TONE: Record<GovernanceResolution["outcome"], Tone> = {
  resolved: "ok",
  ownership_unknown: "quiet",
  ambiguous_ownership: "wait",
  assignment_conflict: "bad",
  ownership_conflict: "bad",
};

const OUTCOME_MEANING: Record<GovernanceResolution["outcome"], string> = {
  resolved: "One effective governance context, for this purpose at this moment.",
  ownership_unknown: "Nothing in the graph reaches a governance context. The enterprise has not said who governs this.",
  ambiguous_ownership:
    "More than one candidate remains and no decision names which governs for this purpose. Refused — picking would hide the ambiguity at the moment it mattered.",
  assignment_conflict:
    "Two decisions are in force for this subject and purpose. Refused rather than reduced: a rule that silently picks between two governance decisions would itself be an ungoverned decision.",
  ownership_conflict: "Nexus and another qualified system disagree.",
};

interface Estate {
  entities: Map<string, CanonicalEntity>;
  byType: Map<CanonicalEntityType, CanonicalEntity[]>;
  relationships: EntityRelationship[];
}

/** One tier of the picture, and the collection it is a window onto. */
interface TierView {
  type: CanonicalEntityType;
  label: string;
  /** The word for one of them, on the node itself. */
  one: string;
  /** The word for many of them, mid-sentence. */
  many: string;
  collection: PagedCollection<CanonicalEntity>;
}

/**
 * Why `projection` is not carried through any of the reads on this screen.
 *
 * `PageOf` — the shape the shared pagination speaks — has `items`,
 * `next_cursor` and `has_more`, and deliberately not `projection`. Dropping a
 * field a screen was handed would be a silent loss, so the proof that there is
 * nothing to lose belongs here, where the next reader will ask for it.
 *
 * All three collections this screen pages answer with `Page.of(...)`:
 *   /core/entities                 src/nexus/core/registry/service.py:458
 *   /core/relationships            src/nexus/core/relationships/service.py:336
 *   /core/governance/assignments   src/nexus/core/relationships/service.py:512
 *
 * `Page.of` (src/nexus/core/contracts/api.py:212-231) takes no `projection`
 * argument and never passes one, so every page it builds keeps the field's
 * `None` default (contracts/api.py:209-210). Across the whole backend there is
 * exactly one site that constructs a non-null projection — the outbox service,
 * src/nexus/core/outbox/service.py:222 — and it serves `/core/events/outbox`,
 * which is not a route this screen reads. `Page` is frozen
 * (`ConfigDict(frozen=True)`, contracts/api.py:203) and no middleware rewrites
 * response bodies, so nothing adds one afterwards either.
 *
 * So `projection` is null on every page this screen reads, and adopting the
 * shared primitive discards nothing. If entities, relationships or governance
 * assignments ever begin answering for their own completeness, these are the
 * readers that would have to stop throwing it away — and the disclosure would
 * belong at the screen boundary, the way EventBackbone states a degraded outbox.
 */
function useTier(type: CanonicalEntityType): PagedCollection<CanonicalEntity> {
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<CanonicalEntity>>("/core/entities", {
        // The tier is the filter. `status: "active"` is what makes this the
        // picture of the estate as it stands rather than everything that was
        // ever registered, and it is not a default the platform would apply.
        params: { entity_type: type, status: "active", limit: GRAPH_PAGE, cursor },
        signal,
      }),
    [type],
  );
  return usePagedCollection<CanonicalEntity>(readPage, { resetKey: type });
}

function useRelationships(): PagedCollection<EntityRelationship> {
  const readPage = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<EntityRelationship>>("/core/relationships", {
        params: { limit: GRAPH_PAGE, cursor },
        signal,
      }),
    [],
  );
  return usePagedCollection<EntityRelationship>(readPage);
}

/**
 * The one state the whole picture is in, from the five collections behind it.
 *
 * A collection reading a *later* page is not the screen loading — the pages
 * already read are on screen and belong there. Only a collection that has
 * nothing yet holds the picture back. A refusal or a failure is reported
 * whenever it happens, because a graph drawn from four tiers where one was
 * denied is not the graph.
 */
function firstBlocking(parts: PagedCollection<unknown>[]): Resource<never> | null {
  for (const part of parts) {
    if (part.state.kind === "ready") continue;
    if (part.state.kind === "loading" && part.items.length > 0) continue;
    return part.state;
  }
  return null;
}

/** A context, in the words somebody would use to talk about it. */
function ContextLine({ estate, context }: { estate: Estate; context: GovernanceContext }) {
  const name = (id: string) => estate.entities.get(id)?.canonical_name;
  const application = name(context.application_id);
  const product = name(context.product_id);
  const organization = name(context.organization_id);
  return (
    <span>
      <strong>{application ?? <Identifier value={context.application_id} />}</strong>
      {" in "}
      {product ?? <Identifier value={context.product_id} />}
      {", owned by "}
      {organization ?? <Identifier value={context.organization_id} />}
    </span>
  );
}


function GovernancePanel({ estate, repository }: { estate: Estate; repository: CanonicalEntity }) {
  const session = useSession();
  const [purpose, setPurpose] = useState("engineering");
  const [asked, setAsked] = useState("engineering");

  const read = useCallback(
    (signal: AbortSignal) =>
      api.get<GovernanceResolution>("/core/governance/resolve", {
        params: { subject_entity_id: repository.entity_id, purpose: asked },
        signal,
      }),
    [repository.entity_id, asked],
  );
  // Not a page. One resolution for one subject at one moment, which either
  // answers or refuses — there is no second half of it to ask for.
  const { state, reload } = useResource<GovernanceResolution>(read, {
    enabled: session.may("governance.resolve"),
  });

  const readDecisions = useCallback(
    (cursor: string | null, signal: AbortSignal) =>
      api.get<Page<GovernanceAssignment>>("/core/governance/assignments", {
        params: { subject_entity_id: repository.entity_id, limit: DECISIONS_PAGE, cursor },
        signal,
      }),
    [repository.entity_id],
  );
  // The subject is what the accumulated pages belong to: another repository's
  // decisions are a different collection, not more of this one's.
  const assignments = usePagedCollection<GovernanceAssignment>(readDecisions, {
    enabled: session.may("governance.read"),
    resetKey: repository.entity_id,
  });

  if (!session.may("governance.resolve")) {
    return <p className="nx-hint">You do not have access to resolve governance.</p>;
  }

  return (
    <>
      <form
        style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 16 }}
        onSubmit={(event) => {
          event.preventDefault();
          setAsked(purpose);
        }}
      >
        <div className="nx-field" style={{ margin: 0, flex: 1 }}>
          <label htmlFor="governance-purpose">Purpose</label>
          <input
            id="governance-purpose"
            className="nx-input"
            value={purpose}
            onChange={(event) => {
              setPurpose(event.target.value);
            }}
          />
          <span className="nx-hint">
            Ownership is resolved for a stated purpose. Two applications sharing a repository can
            each govern it for a different one.
          </span>
        </div>
        <button type="submit" className="nx-button nx-button--primary">
          Resolve
        </button>
      </form>

      <Show resource={state} what="this governance resolution" onRetry={reload}>
        {(answer) => (
          <div style={{ display: "grid", gap: 16 }}>
            <div>
              <Pill tone={OUTCOME_TONE[answer.outcome]}>{answer.outcome.replace(/_/g, " ")}</Pill>
              <p style={{ marginBottom: 0, color: "var(--nx-text-soft)" }}>
                {OUTCOME_MEANING[answer.outcome]}
              </p>
            </div>

            {answer.context ? (
              <Facts>
                <Fact label="Governed by">
                  <ContextLine estate={estate} context={answer.context} />
                </Fact>
                <Fact label="Basis">{answer.basis?.replace(/_/g, " ") ?? "—"}</Fact>
              </Facts>
            ) : null}

            {answer.candidate_contexts.length > 0 ? (
              <div>
                {/*
                  Not a lower bound. The candidates come from the resolution,
                  which considered the whole graph and returned all of them at
                  once — it is not a page, and saying "2+" here would invent an
                  incompleteness the resolver does not have.
                */}
                <p style={{ fontWeight: 600 }}>
                  {answer.candidate_contexts.length} candidates it could not choose between
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, color: "var(--nx-text-soft)" }}>
                  {answer.candidate_contexts.map((candidate) => (
                    <li key={`${candidate.application_id}:${candidate.product_id}`}>
                      <ContextLine estate={estate} context={candidate} />
                    </li>
                  ))}
                </ul>
                <p className="nx-hint">
                  Reconciling this means either withdrawing a relationship that no longer holds, or
                  deciding — for a stated purpose — which of these governs.
                </p>
              </div>
            ) : null}

            {answer.conflicting_assignment_ids.length > 0 ? (
              <div>
                <p style={{ fontWeight: 600 }}>Decisions that conflict</p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {answer.conflicting_assignment_ids.map((id) => (
                    <li key={id}>
                      <Identifier value={id} />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div>
              <p style={{ fontWeight: 600 }}>Evidence</p>
              {answer.relationship_evidence.length === 0 ? (
                <p className="nx-hint">
                  {answer.outcome === "ownership_unknown"
                    ? "No graph facts — nothing reached a governance context."
                    : // Saying "nothing reached a governance context" here would
                      // contradict the candidates named directly above it. A
                      // refusal rests on the candidates, not on one path.
                      "A refusal carries the candidates it could not choose between rather than one path's evidence. The graph facts appear once an answer resolves."}
                </p>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 18, color: "var(--nx-text-soft)" }}>
                  {answer.relationship_evidence.map((fact) => (
                    <li key={fact.relationship_id}>
                      {estate.entities.get(fact.source_entity_id)?.canonical_name ?? "…"}{" "}
                      <Mono>{fact.relationship_type}</Mono>{" "}
                      {estate.entities.get(fact.target_entity_id)?.canonical_name ?? "…"}
                      <span className="nx-hint">
                        {" "}
                        · in force from {when(fact.effective_from)}
                        {fact.effective_to ? ` until ${when(fact.effective_to)}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {answer.discarded_relationship_ids.length > 0 ? (
                <p style={{ color: "var(--nx-bad)" }}>
                  {answer.discarded_relationship_ids.length} relationship
                  {answer.discarded_relationship_ids.length === 1 ? " was" : "s were"} not walked —
                  the stored row does not join the kinds of entity its type joins. Somebody wrote
                  around the API.
                </p>
              ) : null}
              <Facts>
                <Fact label="Resolver">
                  <Mono>{answer.resolver_version}</Mono>
                </Fact>
                <Fact label="Digest">
                  <Identifier value={answer.evidence_digest} />
                </Fact>
                <Fact label="Effective at">{when(answer.effective_at)}</Fact>
              </Facts>
            </div>
          </div>
        )}
      </Show>

      {session.may("governance.read") ? (
        <div style={{ marginTop: 20 }}>
          <p style={{ fontWeight: 600 }}>Decisions on record</p>
          <Show
            resource={assignments.state}
            what="the governance decisions"
            onRetry={assignments.refresh}
            empty={() =>
              assignments.items.length === 0 ? (
                // "None" is a claim about the whole collection, and only the
                // end of the walk earns it. Said from a window that was cut
                // short it would tell somebody the graph alone decides when a
                // decision breaking the tie may be on the next page.
                assignments.hasMore ? (
                  <p className="nx-hint">
                    None on the pages read so far. Decisions are read a page at a time, and whether
                    somebody has broken a tie for a purpose is not yet known.
                  </p>
                ) : (
                  <p className="nx-hint">
                    None. The graph alone decides, unless somebody has broken a tie for a purpose.
                  </p>
                )
              ) : null
            }
          >
            {() => (
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {assignments.items.map((assignment) => (
                  <li key={assignment.assignment_id}>
                    <strong>{assignment.purpose}</strong> — {assignment.rationale}
                    <span className="nx-hint"> · {when(assignment.effective_from)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Show>
          <More
            collection={assignments}
            label="Show more decisions"
            hint="This is one page of the decisions on record for this repository, not all of them."
          />
        </div>
      ) : null}
    </>
  );
}

function NodeDetail({
  estate,
  entity,
  relationships,
}: {
  estate: Estate;
  entity: CanonicalEntity;
  relationships: PagedCollection<EntityRelationship>;
}) {
  const [tab, setTab] = useState("overview");
  const related = estate.relationships.filter(
    (edge) => edge.source_entity_id === entity.entity_id || edge.target_entity_id === entity.entity_id,
  );
  const name = (id: string) => estate.entities.get(id)?.canonical_name ?? id;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "relationships", label: "Relationships" },
    ...(entity.entity_type === "repository" ? [{ id: "governance", label: "Governance" }] : []),
  ];

  return (
    <Card
      title={
        <span>
          {entity.canonical_name}
          <span className="nx-hint" style={{ display: "block", fontWeight: 400 }}>
            {entity.entity_type}
          </span>
        </span>
      }
      bodyless
    >
      <Tabs tabs={tabs} active={tab} onSelect={setTab} label={`${entity.canonical_name} details`} />
      <TabPanel id="overview" active={tab}>
        <Facts>
          <Fact label="Canonical ID">
            <Identifier value={entity.entity_id} />
          </Fact>
          <Fact label="Status">
            <Pill tone={entity.status === "active" ? "ok" : "quiet"}>{entity.status}</Pill>
          </Fact>
          <Fact label="Scope">{entity.scope}</Fact>
          <Fact label="Created">{when(entity.created_at)}</Fact>
          <Fact label="Updated">{when(entity.updated_at)}</Fact>
        </Facts>
      </TabPanel>
      <TabPanel id="relationships" active={tab}>
        {related.length === 0 ? (
          // The edges are read for the whole estate and matched to this entity
          // here, so "none" is a claim about every page, not about the ones in
          // hand. From a window that was cut short it is a false negative — and
          // a false negative about a relationship is what makes an ownership
          // question look settled when it is not.
          <p className="nx-hint">
            {relationships.hasMore
              ? "No relationship on the pages read so far joins this entity. Whether it has any is not yet known."
              : "No relationships in force."}
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {related.map((edge) => (
              <li key={edge.relationship_id}>
                {name(edge.source_entity_id)} <Mono>{edge.relationship_type}</Mono>{" "}
                {name(edge.target_entity_id)}
                <span className="nx-hint"> · from {when(edge.effective_from)}</span>
              </li>
            ))}
          </ul>
        )}
        <More
          collection={relationships}
          label="Show more relationships"
          hint={`The graph reads relationships ${String(GRAPH_PAGE)} at a time and matches them to this entity here, so one it has may be on a page not yet read.`}
        />
      </TabPanel>
      {entity.entity_type === "repository" ? (
        <TabPanel id="governance" active={tab}>
          <GovernancePanel estate={estate} repository={entity} />
        </TabPanel>
      ) : null}
    </Card>
  );
}

export function EnterpriseGraph() {
  const organizations = useTier("organization");
  const products = useTier("product");
  const applications = useTier("application");
  const repositories = useTier("repository");
  const relationships = useRelationships();

  const [selected, setSelected] = useState<string | null>(null);

  const tiers: TierView[] = [
    {
      type: "organization",
      label: "Organizations",
      one: "Organization",
      many: "organizations",
      collection: organizations,
    },
    { type: "product", label: "Products", one: "Product", many: "products", collection: products },
    {
      type: "application",
      label: "Applications",
      one: "Application",
      many: "applications",
      collection: applications,
    },
    {
      type: "repository",
      label: "Repositories",
      one: "Repository",
      many: "repositories",
      collection: repositories,
    },
  ];

  const parts: PagedCollection<unknown>[] = [
    organizations,
    products,
    applications,
    repositories,
    relationships,
  ];

  const estate: Estate = useMemo(() => {
    const entities = new Map<string, CanonicalEntity>();
    const byType = new Map<CanonicalEntityType, CanonicalEntity[]>();
    const read: [CanonicalEntityType, CanonicalEntity[]][] = [
      ["organization", organizations.items],
      ["product", products.items],
      ["application", applications.items],
      ["repository", repositories.items],
    ];
    for (const [type, items] of read) {
      const sorted = [...items].sort((a, b) => a.canonical_name.localeCompare(b.canonical_name));
      byType.set(type, sorted);
      for (const entity of sorted) entities.set(entity.entity_id, entity);
    }
    return { entities, byType, relationships: relationships.items };
  }, [
    organizations.items,
    products.items,
    applications.items,
    repositories.items,
    relationships.items,
  ]);

  const blocking = firstBlocking(parts);
  const state: Resource<Estate> = blocking ?? { kind: "ready", value: estate };

  const reload = () => {
    for (const part of parts) part.refresh();
  };

  const selectedEntity = useMemo(() => {
    if (selected === null) return null;
    return estate.entities.get(selected) ?? null;
  }, [estate, selected]);

  return (
    <Show resource={state} what="the enterprise graph" onRetry={reload}>
      {(value) => (
        <div className="nx-split">
          <Card
            title="Enterprise structure"
            actions={
              <button type="button" className="nx-button" onClick={reload}>
                Refresh
              </button>
            }
            bodyless
          >
            {value.entities.size === 0 ? (
              <Empty
                title="No organizations, products, applications or repositories yet."
                hint="They are canonical entities; a steward creates them, and relationships are asserted between them."
              />
            ) : (
              <div className="nx-graph">
                {tiers.map((tier) => {
                  const nodes = value.byType.get(tier.type) ?? [];
                  return (
                    <div className="nx-graph__tier" key={tier.type}>
                      <span className="nx-graph__tier-label">{tier.label}</span>
                      {/*
                        The tier grid gives the label one column and this one
                        the other, so the row and what is said about the row
                        stay together under the same heading.
                      */}
                      <div style={{ display: "grid", gap: 10 }}>
                        <div className="nx-graph__row">
                          {nodes.length === 0 ? (
                            <span className="nx-hint">none</span>
                          ) : (
                            nodes.map((node) => (
                              <button
                                key={node.entity_id}
                                type="button"
                                className="nx-node"
                                aria-pressed={selected === node.entity_id}
                                onClick={() => {
                                  setSelected(node.entity_id);
                                }}
                              >
                                <span>
                                  <span className="nx-node__name">{node.canonical_name}</span>
                                  <span className="nx-node__kind" style={{ display: "block" }}>
                                    {tier.one}
                                  </span>
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                        <More
                          collection={tier.collection}
                          label={`Show more ${tier.many}`}
                          hint={`The graph reads ${String(GRAPH_PAGE)} ${tier.many} at a time. This is one page of them, not every ${tier.one.toLowerCase()} there is.`}
                          placement="flush"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {selectedEntity ? (
            <NodeDetail estate={value} entity={selectedEntity} relationships={relationships} />
          ) : (
            <Card title="Select something">
              <p className="nx-hint">
                Choose an organization, product, application or repository to see what it is
                related to. Governance is resolved for a repository, which is the protected
                execution case.
              </p>
            </Card>
          )}
        </div>
      )}
    </Show>
  );
}
